import {
  ModalForm,
  type ProColumns,
  ProFormDateTimePicker,
  ProFormRadio,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
  ProTable,
} from '@ant-design/pro-components';
import { history } from '@umijs/max';
import { App, Button, Empty, Space, Tag, Typography } from 'antd';
import React from 'react';
import { useBusiness, useCommand } from '@/prototype/context';
import { allowed, type Command } from '@/prototype/service';
import {
  type Entity,
  type Kind,
  label,
  teacherName,
  teachers,
} from '@/prototype/types';
export function Status({ value }: { value: string }) {
  return (
    <Tag
      color={
        ['failed', 'excluded', 'returned'].includes(value)
          ? 'error'
          : ['completed', 'closed', 'succeeded', 'published'].includes(value)
            ? 'success'
            : ['pendingReview', 'confirming', 'needsInfo'].includes(value)
              ? 'warning'
              : 'default'
      }
    >
      {label(value)}
    </Tag>
  );
}
export const links: Partial<Record<Kind, string>> = {
  device: '/device',
  recording: '/recording',
  clue: '/review/clue',
  case: '/case',
  job: '/analysis/result',
  batch: '/collection/batch',
};
export function entityPath(row: Entity): string {
  if (row.kind === 'plan') return `/teaching/plan?planUuid=${row.uuid}`;
  if (row.kind === 'followUp')
    return `/service/follow-up?followUpUuid=${row.uuid}`;
  if (row.kind === 'feedback')
    return `/service/feedback?feedbackUuid=${row.uuid}`;
  if (row.kind === 'task') return `/task?task.uuid=${row.uuid}`;
  if (row.kind === 'message') return `/message?message.uuid=${row.uuid}`;
  if (row.kind === 'teacher') return `/teacher/${row.memberUuid}`;
  if (links[row.kind]) return `${links[row.kind]}/${row.uuid}`;
  const path: Partial<Record<Kind, string>> = {
    knowledge: '/knowledge',
    lesson: '/teaching/lesson',
    pilot: '/pilot',
    student: '/student',
  };
  if (path[row.kind]) return `${path[row.kind]}/${row.uuid}`;
  const collections: Partial<Record<Kind, string>> = {
    plan: '/teaching/plan',
    followUp: '/service/follow-up',
    feedback: '/service/feedback',
    ticket: '/support/ticket',
    repair: '/device/repair',
    inventory: '/device/inventory',
    template: '/analysis/template',
    rule: '/system/rule',
    correction: '/review/correction',
    integration: '/system/integration',
    organization: '/system/organization',
    policy: '/system/permission',
    modelConfig: '/system/model',
    subscription: '/system/subscription',
    zone: '/location/zone',
    help: '/support/help',
    station: '/collection/station',
    model: '/device/model',
  };
  return `${collections[row.kind] || '/task'}?objectUuid=${row.uuid}`;
}
export function EntityLink({ uuid }: { uuid?: string }) {
  const { service } = useBusiness();
  const row = service?.get(uuid);
  return row ? (
    <Button type="link" onClick={() => history.push(entityPath(row))}>
      {row.title}
    </Button>
  ) : (
    <Typography.Text type="secondary">无关联或无权访问</Typography.Text>
  );
}
export function CommandButton({
  action,
  row,
  children,
  initial = {},
  onSaved,
  disabled = false,
}: {
  action: string;
  row?: Entity;
  children: React.ReactNode;
  initial?: Partial<Command>;
  onSaved?: (uuid: string) => void;
  disabled?: boolean;
}) {
  const { state } = useBusiness();
  const { run, busy } = useCommand();
  const { modal } = App.useApp();
  const [open, setOpen] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const noteLabels: Record<string, string> = {
    bind: '领用说明',
    review: '复核意见',
    submit: '处理结果',
    close: '验收意见',
    reopen: '重新打开原因',
    task: '完成说明',
  };
  const needsOwner = ['bind', 'assign', 'case'].includes(action);
  const needsNote = ['review', 'submit', 'close', 'reopen', 'task'].includes(
    action,
  );
  const hasForm = needsOwner || needsNote || action === 'clue';
  const noPermission = !state || !allowed(action, state.context);
  const trigger = (
    <Button
      disabled={disabled || noPermission}
      loading={busy}
      title={noPermission ? '当前业务角色无此操作权限' : undefined}
      onClick={() => {
        if (hasForm) setOpen(true);
        else
          void run({ action, uuid: row?.uuid, ...initial }).then((uuid) => {
            if (uuid !== undefined) onSaved?.(uuid);
          });
      }}
    >
      {children}
    </Button>
  );
  if (!hasForm) return trigger;
  return (
    <>
      {trigger}
      <ModalForm<Omit<Command, 'action' | 'meta'>>
        title={children}
        open={open}
        modalProps={{
          destroyOnHidden: true,
          onCancel: () => {
            if (dirty)
              modal.confirm({
                title: '放弃未提交内容？',
                content: '关闭后可以重新打开并从对象操作开始。',
                onOk: () => {
                  setOpen(false);
                  setDirty(false);
                },
              });
            else setOpen(false);
          },
        }}
        onValuesChange={() => setDirty(true)}
        initialValues={{
          memberUuid: row?.owner || teachers[0],
          title: row?.title,
          dueAt: '2026-10-23 18:00:00',
          conclusion: '信息不足',
          ...initial,
        }}
        onFinish={async (values) => {
          const uuid = await run({
            action,
            uuid: row?.uuid,
            ...initial,
            ...values,
            dueAt: values.dueAt
              ? new Date(values.dueAt).toISOString()
              : undefined,
            meta: {
              commandUuid: crypto.randomUUID(),
              expectedRevision: row?.revision,
            },
          });
          if (uuid !== undefined) {
            setOpen(false);
            setDirty(false);
            onSaved?.(uuid);
            return true;
          }
          return false;
        }}
      >
        {['case', 'clue'].includes(action) && (
          <ProFormText
            name="title"
            label="标题"
            rules={[{ required: true, max: 100 }]}
          />
        )}
        {needsOwner && (
          <ProFormSelect
            name="memberUuid"
            label={action === 'bind' ? '使用人' : '负责人'}
            options={teachers.map((value) => ({
              value,
              label: teacherName(value),
            }))}
            rules={[{ required: true }]}
          />
        )}
        {action === 'bind' && (
          <ProFormDateTimePicker
            name="startAt"
            label="生效时间"
            initialValue={state?.now}
            rules={[{ required: true }]}
          />
        )}
        {['assign', 'case'].includes(action) && (
          <ProFormDateTimePicker
            name="dueAt"
            label="截止时间"
            rules={[{ required: true }]}
          />
        )}
        {action === 'review' && (
          <ProFormRadio.Group
            name="conclusion"
            label="人工结论"
            options={['成立', '部分成立', '不成立', '信息不足']}
            rules={[{ required: true }]}
          />
        )}
        {(needsNote || action === 'clue') && (
          <ProFormTextArea
            name="note"
            label={noteLabels[action] || '说明'}
            fieldProps={{ rows: 4, maxLength: 2000 }}
            rules={[{ required: needsNote }]}
          />
        )}
      </ModalForm>
    </>
  );
}
export function BusinessTable({
  kind,
  extra = [],
  actions,
  title,
  rows,
  toolbar,
}: {
  kind: Kind;
  extra?: ProColumns<Entity>[];
  actions?: (row: Entity) => React.ReactNode;
  title?: string;
  rows?: Entity[];
  toolbar?: React.ReactNode[];
}) {
  const { service, state } = useBusiness();
  const { run, busy } = useCommand();
  const [selectedKeys, setSelectedKeys] = React.useState<React.Key[]>([]);
  React.useEffect(
    () => setSelectedKeys([]),
    [kind, state?.context.role, state?.context.campus],
  );
  const initialFilter = Object.fromEntries(
    [...new URLSearchParams(window.location.search)]
      .filter(([key]) => key.startsWith(`${kind}.`))
      .map(([key, value]) => [key.slice(kind.length + 1), value]),
  );
  const [filter, setFilter] =
    React.useState<Record<string, string>>(initialFilter);
  const data = rows || service?.list(kind) || [];
  const filtered = data.filter(
    (row) =>
      (!filter.uuid || row.uuid === filter.uuid) &&
      (!filter.title || row.title.includes(filter.title)) &&
      (!filter.status || row.status === filter.status) &&
      (!filter.memberUuid || row.memberUuid === filter.memberUuid),
  );
  const statuses = Array.from(new Set(data.map((row) => row.status))).map(
    (value) => ({ value, label: label(value) }),
  );
  const columns: ProColumns<Entity>[] = [
    {
      title: '名称',
      dataIndex: 'title',
      ellipsis: true,
      width: 240,
      render: (_, row) =>
        links[kind] ? (
          <Button
            type="link"
            onClick={() => history.push(`${links[kind]}/${row.uuid}`)}
          >
            {row.title}
          </Button>
        ) : (
          row.title
        ),
    },
    { title: '校区', dataIndex: 'campus', search: false, width: 120 },
    {
      title: '关联人员',
      dataIndex: 'memberUuid',
      width: 110,
      valueType: 'select',
      fieldProps: {
        options: teachers.map((value) => ({
          value,
          label: teacherName(value),
        })),
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 130,
      valueType: 'select',
      fieldProps: { options: statuses },
      render: (_, row) => <Status value={row.status} />,
    },
    ...extra,
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      valueType: 'dateTime',
      search: false,
      sorter: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
      width: 180,
    },
    ...(actions
      ? [
          {
            title: '操作',
            valueType: 'option' as const,
            width: 260,
            fixed: 'right' as const,
            render: (_: unknown, row: Entity) => (
              <Space wrap>{actions(row)}</Space>
            ),
          },
        ]
      : []),
  ];
  return (
    <ProTable<Entity>
      key={`${kind}-${state?.context.role}-${state?.context.campus}`}
      rowKey="uuid"
      rowSelection={{
        selectedRowKeys: selectedKeys,
        onChange: setSelectedKeys,
        preserveSelectedRowKeys: false,
      }}
      headerTitle={title}
      columns={columns}
      dataSource={filtered}
      onSubmit={(values) => {
        setFilter(values);
        const query = new URLSearchParams(window.location.search);
        for (const key of [...query.keys()])
          if (key.startsWith(`${kind}.`)) query.delete(key);
        for (const [key, value] of Object.entries(values))
          if (value !== undefined && value !== '')
            query.set(`${kind}.${key}`, String(value));
        history.replace(`${window.location.pathname}?${query}`);
      }}
      onReset={() => {
        setFilter({});
        setSelectedKeys([]);
        const query = new URLSearchParams(window.location.search);
        for (const key of [...query.keys()])
          if (key.startsWith(`${kind}.`)) query.delete(key);
        history.replace(`${window.location.pathname}?${query}`);
      }}
      form={{ variant: 'filled', initialValues: initialFilter }}
      search={{ defaultCollapsed: true, labelWidth: 'auto' }}
      pagination={{
        defaultPageSize: 20,
        showSizeChanger: true,
        onChange: () => setSelectedKeys([]),
        pageSizeOptions: [10, 20, 50],
      }}
      scroll={{ x: 1250 }}
      options={{
        density: true,
        setting: true,
        reload: () => service?.refresh(),
      }}
      toolBarRender={() => [
        ...(toolbar || []),
        ...(selectedKeys.length
          ? [
              <Button
                key="export"
                loading={busy}
                disabled={!state || !allowed('exportMetadata', state.context)}
                onClick={async () => {
                  const rows = filtered.filter((row) =>
                    selectedKeys.includes(row.uuid),
                  );
                  if (!rows.length) return;
                  const uuid = await run({
                    action: 'exportMetadata',
                    kind,
                    fields: {
                      ids: JSON.stringify(rows.map((row) => row.uuid)),
                    },
                  });
                  if (uuid === undefined) return;
                  const cell = (value: string) =>
                    `"${(/^[=+@-]/.test(value) ? "'" : '') + value.replace(/"/g, '""')}"`;
                  const data = [
                    ['编号', '名称', '校区', '人员', '状态'],
                    ...rows.map((row) => [
                      row.uuid,
                      row.title,
                      row.campus,
                      teacherName(row.memberUuid),
                      label(row.status),
                    ]),
                  ]
                    .map((row) => row.map(cell).join(','))
                    .join('\r\n');
                  const url = URL.createObjectURL(
                    new Blob([`\uFEFF${data}`], {
                      type: 'text/csv;charset=utf-8',
                    }),
                  );
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `智能录音工牌系统-${kind}-选中记录.csv`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                }}
              >
                导出选中 {selectedKeys.length} 条
              </Button>,
            ]
          : []),
      ]}
      locale={{
        emptyText: (
          <Empty
            description={
              data.length ? '没有匹配结果' : '暂无数据，请通过相关业务操作创建'
            }
          />
        ),
      }}
    />
  );
}
